import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { MessageCircleReply, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useAccounts } from '@/api/hooks/useAccounts'
import { getAuthHeaders } from '@/api/client'

// Rotas do Express (v1): toggle por página e liga/desliga em massa
async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error('Falha na requisição')
  return res.json()
}

export function RespostaDM() {
  const { data: accounts = [], isLoading } = useAccounts()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const pages = useMemo(() => accounts.filter((a) => !!a.pageId), [accounts])
  const filtered = useMemo(
    () => pages.filter((a) => (a.name || '').toLowerCase().includes(search.trim().toLowerCase())),
    [pages, search],
  )
  const total = pages.length
  const enabled = pages.filter((a) => !!a.dmAutoCard).length

  async function toggle(igUserId: string, label: string) {
    setBusy(igUserId)
    try {
      await post(`/accounts/${igUserId}/toggle-dm-card`)
      await qc.invalidateQueries({ queryKey: ['accounts'] })
    } catch {
      toast.error(`Falha ao atualizar ${label}`)
    } finally {
      setBusy(null)
    }
  }

  async function bulk(on: boolean) {
    setBusy('bulk')
    try {
      const r = await post<{ updated: number }>('/accounts/bulk-dm-card', { enabled: on })
      await qc.invalidateQueries({ queryKey: ['accounts'] })
      toast.success(`${on ? 'Ligado' : 'Desligado'} em ${r.updated} página(s)`)
    } catch {
      toast.error('Falha na ação em massa')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <MessageCircleReply className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold">Resposta no DM</h1>
      </div>
      <p className="text-muted-foreground mb-6 text-sm">
        Quando alguém te manda uma mensagem no direct, a página responde automaticamente com o{' '}
        <strong>card de captura</strong> (foto + texto + link) — os mesmos cards do comentário. Cooldown de{' '}
        <strong>24h por pessoa</strong>, então ninguém recebe duas vezes no mesmo dia. Ligue por página abaixo.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar página..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={() => bulk(true)} disabled={busy === 'bulk'}>
          Ligar todas
        </Button>
        <Button variant="outline" onClick={() => bulk(false)} disabled={busy === 'bulk'}>
          Desligar todas
        </Button>
      </div>

      <div className="text-sm text-muted-foreground mb-3">
        <strong className="text-foreground">{enabled}</strong> de {total} páginas com resposta no DM ligada
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Carregando páginas...</div>
      ) : (
        <div className="divide-y rounded-lg border bg-card">
          {filtered.map((a) => (
            <div key={a.igUserId || a.pageId} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 pr-3">
                <div className="font-medium truncate">{a.name || a.pageId}</div>
                <div className="text-xs text-muted-foreground truncate">{a.pageId}</div>
              </div>
              <Switch
                checked={!!a.dmAutoCard}
                disabled={busy === a.igUserId}
                onCheckedChange={() => toggle(a.igUserId, a.name || a.pageId)}
              />
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-muted-foreground">Nenhuma página encontrada.</div>
          )}
        </div>
      )}
    </div>
  )
}
